import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuIcon, XIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { Logo } from './Logo';
import { ArrowButton } from '../ui/ArrowButton';
import { navLinks, contactInfo } from '../../data/navigation';
import { EASE_OUT } from '../../utils/motion';
export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  return <header className={`fixed inset-x-0 top-0 z-50 bg-ink transition-shadow duration-200 ${scrolled ? 'shadow-[0_1px_0_rgba(255,255,255,0.08)]' : ''}`} data-id="element-366" data-mp="3boqvn8trfw1f">
      <div className={`mx-auto flex max-w-[1440px] items-center justify-between gap-6 px-5 transition-[height] duration-200 ease-out-strong sm:px-8 ${scrolled ? 'h-16' : 'h-[76px]'}`} data-id="element-367" data-mp="32njaztk62tpt">
        <Logo data-id="element-368" data-mp="2srzep61oxt0j" />

        <nav aria-label="Navigation principale" className="hidden xl:block" data-id="element-369" data-mp="2ssiyksawirh7">
          <ul className="flex items-center gap-1" data-id="element-370" data-mp="2ny502h0y048b">
            {navLinks.map(link => <li key={link.to} data-id="element-371" data-mp="2jyw89hpgqgfe">
                <NavLink to={link.to} end={link.to === '/'} className={({
              isActive
            }) => `relative block whitespace-nowrap rounded-lg px-3.5 py-2 text-[15px] transition-colors duration-150 ${isActive ? 'text-white' : 'text-white/65 hover:text-white'}`} data-id="element-372" data-mp="2jw04vduuizjk">
                  {({
                isActive
              }) => <>
                      {isActive && <motion.span layoutId="nav-active" className="absolute inset-0 rounded-lg bg-white/10" transition={{
                  duration: 0.25,
                  ease: EASE_OUT
                }} data-id="element-373" data-mp="1mcjkiy5elz70" />}
                      <span className="relative" data-id="element-374" data-mp="2e622lrqvu79c">{link.label}</span>
                    </>}
                </NavLink>
              </li>)}
          </ul>
        </nav>

        <div className="flex items-center gap-3" data-id="element-375" data-mp="259ba1pbjhaji">
          <ArrowButton to="/contact" variant="light" className="hidden sm:inline-flex" data-id="element-376" data-mp="29c7pj75s8qj8">
            Prendre rendez-vous
          </ArrowButton>
          <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'} className="grid h-11 w-11 place-items-center rounded-xl bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 xl:hidden" data-id="element-377" data-mp="2x0h2rkoxk5yr">
            {open ? <XIcon className="h-5 w-5" data-id="element-378" data-mp="1067fl3s4y1x9" /> : <MenuIcon className="h-5 w-5" data-id="element-379" data-mp="v4hz38m58w1s" />}
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-380" data-mp="7fgujwgfm51s">
        {open && <motion.div id="mobile-menu" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.22,
        ease: EASE_OUT
      }} className="fixed inset-x-0 bottom-0 top-[64px] overflow-y-auto bg-ink px-5 pb-10 pt-6 sm:px-8 xl:hidden" data-id="element-381" data-mp="7g8j4nbm32ik">
            <nav aria-label="Navigation mobile" data-id="element-382" data-mp="1ssa7yzukdlp7">
              <ul className="divide-y divide-white/10 border-y border-white/10" data-id="element-383" data-mp="1sqyx6g2pbdol">
                {navLinks.map((link, i) => <motion.li key={link.to} initial={{
              opacity: 0,
              x: -12
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.25,
              ease: EASE_OUT,
              delay: 0.04 * i
            }} data-id="element-384" data-mp="3thzf9h6z0u1h" data-mpi={i}>
                    <NavLink to={link.to} end={link.to === '/'} className={({
                isActive
              }) => `block py-4 text-2xl font-medium ${isActive ? 'text-brass' : 'text-white'}`} data-id="element-385" data-mp="6fe4qg274kxt">
                      {link.label}
                    </NavLink>
                  </motion.li>)}
              </ul>
            </nav>
            <div className="mt-8 space-y-3 text-white/70" data-id="element-386" data-mp="u343jmuwju42">
              <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-3" data-id="element-387" data-mp="u4ovr5tqv0yr">
                <MailIcon className="h-4 w-4" data-id="element-388" data-mp="yzuk0bamcrq0" /> {contactInfo.email}
              </a>
              <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-3" data-id="element-389" data-mp="141bf1q6f9jyo">
                <PhoneIcon className="h-4 w-4" data-id="element-390" data-mp="1e2vvsrwbi6cg" /> {contactInfo.phone}
              </a>
            </div>
            <Link to="/contact" className="mt-8 flex h-14 items-center justify-center rounded-xl bg-brass text-base font-medium text-ink" data-id="element-391" data-mp="197yrtzdwan6f">
              Prendre rendez-vous
            </Link>
          </motion.div>}
      </AnimatePresence>
    </header>;
}